import { Link } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { ShippingPaperworkRowFragment } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  formatShippingDocumentDate,
  NO_VALUE_PLACEHOLDER,
  SHIPPING_DOCUMENT_KIND_LABELS,
} from "routes/Customer/Waste/ShippingDocuments/shippingDocumentDisplay";
import { parseOmnisearchTokens } from "utils/omnisearchUtils";

import type { Row } from "./Manifests";

/** One item straight off the `shippingPaperwork` query, before flattening. */
export type ShippingPaperworkItem = ShippingPaperworkRowFragment;

/**
 * A grid row for the merged Manifests list.
 *
 * V2's column definitions read manifest fields off the row itself
 * (`row.epaId`, `row.discrepancyTally`, …), so the manifest side is spread onto
 * the item before the row reaches the grid. That is what lets the merged list
 * render V2's `useManifestColumns` output unmodified instead of a parallel
 * column set. Being a subtype of `Row`, it is also what the guards below can
 * narrow a plain `Row` to, which is how a column typed on V2's row still reads
 * `billOfLading`.
 */
export type ShippingPaperworkRow = Row & Omit<ShippingPaperworkItem, "__typename">;

/** A row whose manifest side is populated. */
export type ManifestRow = ShippingPaperworkRow & {
  manifest: NonNullable<ShippingPaperworkItem["manifest"]>;
};
/** A row whose bill-of-lading side is populated. */
export type BillOfLadingRow = ShippingPaperworkRow & {
  billOfLading: NonNullable<ShippingPaperworkItem["billOfLading"]>;
};

/**
 * Whether this row came from the merged list at all. V2's own Manifests grid
 * hands its columns a plain `Row` with no paperwork fields, so the narrowing
 * starts from the key only the merged list's rows carry.
 */
export function isPaperworkRow(row: Row): row is ShippingPaperworkRow {
  return "paperworkKind" in row;
}

export function isManifestRow(row: Row): row is ManifestRow {
  return "manifest" in row && row.manifest != null;
}

export function isBillOfLadingRow(row: Row): row is BillOfLadingRow {
  return "billOfLading" in row && row.billOfLading != null;
}

/**
 * The Type column's label. A manifest row always reads "Manifest"; a
 * shipping-document row reads its own `kind` through
 * `SHIPPING_DOCUMENT_KIND_LABELS`, so a document a user marked as a manifest
 * renders as "Manifest" rather than "Bill of Lading".
 *
 * `paperworkKind` on the item now carries the same value, and the `kind:`
 * filter matches on it, so the filter and this label agree. Read off the
 * populated side rather than off `paperworkKind` because the two hydrated row
 * shapes are what the rest of this file narrows on.
 */
export function paperworkTypeLabel(row: Row): string {
  if (isManifestRow(row)) {
    return "Manifest";
  }
  if (isBillOfLadingRow(row)) {
    return SHIPPING_DOCUMENT_KIND_LABELS[row.billOfLading.kind];
  }
  return "";
}

/**
 * An all-zero tally and an empty source list, so a bill-of-lading row can
 * satisfy the two non-nullable `Row` fields it has no manifest to supply.
 * Never rendered: `blankOnBillOfLading` below drops every manifest cell on a
 * bill-of-lading row before these reach a component.
 */
const EMPTY_DISCREPANCY_TALLY: Row["discrepancyTally"] = {
  complianceTotal: 0,
  missingEManifest: 0,
  epaCorrection: 0,
  epaCorrectionPreviewLines: [],
  fieldTotal: 0,
  fieldUnresolved: 0,
  fieldPending: 0,
  fieldResolved: 0,
};

/**
 * Flattens one query item into a grid row.
 *
 * The item is spread LAST so its own `id` (the composite `<kind>:<entity id>`
 * the grid keys rows by) wins over the manifest's entity id, and so both
 * `manifest` and `billOfLading` survive for the row-click routing and the
 * drawer hooks, which read the entity id off the populated side.
 *
 * The four `Row` fields a bill of lading has no manifest for are filled from
 * the item's own equivalents rather than invented: `manifestTrackingNumber`
 * from the item's `documentNumber` (the same value the merged view sorts and
 * filters as `documentNumber` for both kinds) and `shippedDate` from
 * `shipDate`. That is what makes V2's Manifest # column read a bill of lading's
 * document number instead of a blank.
 */
export function buildPaperworkRow(item: ShippingPaperworkItem): ShippingPaperworkRow {
  return {
    manifestTrackingNumber: item.documentNumber ?? NO_VALUE_PLACEHOLDER,
    shippedDate: item.shipDate,
    availableSources: [],
    discrepancyTally: EMPTY_DISCREPANCY_TALLY,
    ...item.manifest,
    ...item,
    // Two source types are being folded into one row, so neither typename
    // describes it. Dropped rather than left claiming to be one of them.
    __typename: undefined,
  };
}

/**
 * The entity id a drawer or a mutation needs, off the row's populated side.
 * Never the row's own `id`: that is `<kind>:<entity id>`, unique across the two
 * source tables' independent uuid spaces but not a valid id for either.
 */
export function paperworkManifestId(row: Row): string | null {
  return isManifestRow(row) ? row.manifest.id : null;
}

/** A manifest cell's output on a manifest row, and nothing on a bill of lading. */
export function manifestCellOnly<P extends { row: Row }>(
  renderCell: (params: P) => ReactNode,
  params: P
): ReactNode {
  return isManifestRow(params.row) ? renderCell(params) : null;
}

/**
 * V2's columns all describe a manifest. Rather than edit them one by one, each
 * cell renderer is wrapped so a bill-of-lading row renders blank — the same
 * guard the earlier merged column set applied per cell, applied once here.
 *
 * Only `renderCell` is wrapped. The plain value columns carry a real value on
 * both kinds and are left to render it: Manifest #, Facility, and Ship Date,
 * which `buildPaperworkRow` fills from `item.shipDate` for either kind. Ship
 * Date reaching a bill-of-lading row is why it is absent from
 * `INITIAL_COLUMN_VISIBILITY` below, so the column the list is ordered by is
 * visible on both kinds.
 */

export function blankOnBillOfLading(columns: OmnisearchGridColDef<Row>[]) {
  return columns.map((column) => {
    const renderCell = column.renderCell;
    if (!renderCell) {
      return column;
    }
    return {
      ...column,
      renderCell: (params: Parameters<typeof renderCell>[0]) =>
        manifestCellOnly(renderCell, params),
    };
  });
}

/**
 * A bill of lading's party name, or null on a manifest row. Shared by the three
 * party columns below and exported so a test can call it with a row alone:
 * read off a column, `valueGetter`'s declared MUI type pins its first parameter
 * to `never`, which no test can supply without an unsound cast.
 */
export function billOfLadingPartyName(
  row: Row,
  field: "generatorName" | "transporterName" | "destinationName"
): string | null {
  return isBillOfLadingRow(row) ? (row.billOfLading[field] ?? NO_VALUE_PLACEHOLDER) : null;
}

/**
 * Filter and sort keys V2's columns use, mapped to the equivalent column on
 * `ShippingPaperworkView`. The merged view stores one document number and one
 * ship date for both kinds, under names of its own.
 */
export const PAPERWORK_KEY_ALIASES: Record<string, string> = {
  manifestTrackingNumber: "documentNumber",
  shippedDate: "shipDate",
};

/**
 * Columns the merged view can order by. The `shippingPaperwork` resolver
 * accepts a fixed set of view columns and silently falls back to ship date for
 * anything else, so every other V2 column is marked unsortable below rather
 * than left offering a sort arrow that does nothing.
 */
const SORTABLE_FIELDS = new Set([
  "manifestTrackingNumber",
  "shippedDate",
  "generatorName",
  "transporterName",
]);

/**
 * Omnisearch keys the merged view does not register, so a filter on them would
 * match every row instead of narrowing. `OmnisearchManager` skips an unknown
 * key silently, which is exactly the "filter that appears to work and does
 * nothing" this drops instead.
 *
 * `sourceDocumentState` and `hasSource` came off this list once the merged
 * view could answer them through its `processedManifest` relation. Both are
 * manifest concepts, so both narrow the list to manifests.
 */
// `destinationName` and `createdAt` join `wasteLineCount` here for a different
// reason. The view populates both on the manifest arm (the migration selects
// `pmv."destinationFacilityName"` and `pmv."createdAt"`), so a filter or sort on
// them does match manifest rows — but their cells read only the bill-of-lading
// side, so a manifest comes back with the cell blank and nothing on the row
// explaining the match. Offering neither control is the honest half of the fix.
// The other half would be to carry the view's values onto `ShippingPaperworkItem`
// so the cells can show what matched; that is an API change and belongs with the
// column work rather than here.
const UNSUPPORTED_FILTER_FIELDS = new Set(["wasteLineCount", "destinationName", "createdAt"]);

/**
 * Applies the merged view's sort and filter support to V2's columns: no sort
 * arrow on a column the view cannot order by, no filter entry for a key it
 * does not register.
 */
export function withPaperworkQuerySupport(
  columns: OmnisearchGridColDef<Row>[]
): OmnisearchGridColDef<Row>[] {
  return columns.map((column) => ({
    ...column,
    sortable: SORTABLE_FIELDS.has(column.field) && column.sortable !== false,
    filterable: UNSUPPORTED_FILTER_FIELDS.has(column.field) ? false : column.filterable,
    // V2's Line Items column reads the manifest side, which is undefined on a
    // bill of lading, so the cell read blank while the fragment was already
    // fetching `billOfLading.lineCount`. Spend the field rather than drop it:
    // the column now carries a real count for both kinds, the way Manifest #
    // and Facility already do.
    ...(column.field === "wasteLineCount"
      ? {
          valueGetter: (_: never, row: Row) =>
            isBillOfLadingRow(row) ? row.billOfLading.lineCount : row.wasteLineCount,
        }
      : {}),
  }));
}

/** Grid sort model to the field names the `shippingPaperwork` resolver accepts. */
export function toPaperworkSortModel(sort: GridSortModel) {
  return sort.map((entry) => ({
    field: PAPERWORK_KEY_ALIASES[entry.field] ?? entry.field,
    sort: entry.sort,
  }));
}

/**
 * The merged list's search string in `processedManifests` vocabulary, for the
 * manifest export.
 *
 * The export runs the V2 manifest query, which speaks the manifest table's key
 * names and knows nothing of the merged view — so the view's names are mapped
 * back, and `kind:` (which selects between the two source tables) is dropped
 * because there is only one table on the other side. An export therefore
 * covers the manifest rows of the current filter, never the bills of lading.
 */
export function toManifestExportSearch(search: string): string {
  const inverseAliases = new Map(
    Object.entries(PAPERWORK_KEY_ALIASES).map(([manifestKey, viewKey]) => [viewKey, manifestKey])
  );
  return parseOmnisearchTokens(search)
    .filter((token) => token.length > 0 && !token.startsWith("kind:"))
    .map((token) => {
      const colonIdx = token.indexOf(":");
      if (colonIdx <= 0) return token;
      const key = token.substring(0, colonIdx);
      const manifestKey = inverseAliases.get(key);
      return manifestKey ? `${manifestKey}${token.substring(colonIdx)}` : token;
    })
    .join(" ");
}

const minWidth = 120;

/**
 * The Type column, exported on its own (not just embedded in
 * `BILL_OF_LADING_COLUMNS`) so a test can call `kindColumn.valueGetter`
 * directly against a real row and get a real string back. Read off an array
 * instead, `valueGetter`'s declared MUI type pins its first parameter to
 * `never` (no value has ever been computed yet) — callable in production (the
 * grid never passes one in), but not callable from a test without an unsound
 * cast. Left un-annotated here so the object keeps its own literal, callable
 * type; the array below still checks it is assignable to the interface.
 */
export const kindColumn = {
  field: "kind",
  headerName: "Type",
  minWidth: 130,
  flex: 0.6,
  sortable: false,
  // `kind` is registered as an omnisearch filter key rather than a column
  // filter, so the Type filter round-trips through the URL.
  filterable: false,
  valueGetter: (_: unknown, row: Row) => paperworkTypeLabel(row),
};

/**
 * The bill-of-lading side's own columns, which have no V2 equivalent. Each
 * reads only from `billOfLading`, so a manifest row leaves them blank. All
 * four are registered omnisearch keys on the merged view and are among the
 * columns the resolver can sort by.
 */
export const BILL_OF_LADING_COLUMNS: OmnisearchGridColDef<Row>[] = [
  {
    field: "generatorName",
    headerName: "Generator",
    minWidth,
    flex: 1,
    valueGetter: (_, row) => billOfLadingPartyName(row, "generatorName"),
  },
  {
    field: "transporterName",
    headerName: "Transporter",
    minWidth,
    flex: 1,
    valueGetter: (_, row) => billOfLadingPartyName(row, "transporterName"),
  },
  {
    field: "destinationName",
    headerName: "Destination",
    minWidth,
    flex: 1,
    valueGetter: (_, row) => billOfLadingPartyName(row, "destinationName"),
  },
  {
    field: "createdAt",
    headerName: "Created",
    minWidth,
    flex: 0.7,
    filterKeyType: "date",
    valueGetter: (_, row) =>
      isBillOfLadingRow(row) ? formatShippingDocumentDate(row.billOfLading.createdAt) : null,
  },
];

/**
 * Type VISIBLE by default: with both kinds interleaved and no Type column, a
 * bill of lading and a manifest are indistinguishable. Generator, Transporter,
 * Destination and Created only ever carry a value on a bill-of-lading row, so
 * they are hidden by default rather than reading empty on most rows. Every
 * other entry mirrors V2's own `initialColumnVisibilityModel`, so the two
 * screens open on the same columns.
 */
/**
 * The shipment a manifest or bill of lading is attached to.
 *
 * Not sortable: the link lives on `WasteShipment`, not on the view this list is
 * built from, so it is resolved per page during hydration rather than being an
 * index column. Not filterable for the same reason.
 *
 * Hidden by default. Most readers come to this list for the paperwork itself,
 * and the link only matters when someone is chasing which shipment a document
 * belongs to.
 */
export function linkedShipmentValue(row: Row): string {
  return row.linkedShipment?.manifestRequestNumber ?? NO_VALUE_PLACEHOLDER;
}

/**
 * Takes the tenant because the cell is a link, and the route is tenant-scoped.
 * Every other cross-reference in this feature is followable: the reconciliation
 * tooltip names the shipment, the rail renders the document number as a link
 * into its drawer. Someone who turns this column on is chasing which shipment a
 * document belongs to, so plain text left them retyping the number into the
 * shipments list.
 */
export function LinkedShipmentCell({ tenantId, row }: { tenantId: string; row: Row }) {
  const shipment = row.linkedShipment;
  if (!shipment) {
    return NO_VALUE_PLACEHOLDER;
  }
  return (
    <Link
      component={RouterLink}
      to={`/o/${tenantId}/waste/shipments/${shipment.id}`}
      // The grid row has its own click handler, which opens the paperwork
      // drawer. Without this, following the link opens both.
      onClick={(event) => event.stopPropagation()}
    >
      {shipment.manifestRequestNumber}
    </Link>
  );
}

export function linkedShipmentColumn(tenantId: string): OmnisearchGridColDef<Row> {
  return {
    field: "linkedShipment",
    headerName: "Linked shipment",
    minWidth,
    flex: 0.8,
    sortable: false,
    filterable: false,
    valueGetter: (_, row) => linkedShipmentValue(row),
    renderCell: ({ row }) => <LinkedShipmentCell tenantId={tenantId} row={row} />,
  };
}

export const INITIAL_COLUMN_VISIBILITY = {
  // Hidden until someone is chasing which shipment a document belongs to.
  linkedShipment: false,
  // `shippedDate` is deliberately absent: it is the sort key, and it is the one
  // date a bill of lading always carries.
  generatorName: false,
  // Filter-only, with no cell of its own — V2 forces it hidden in its own
  // visibility model for the same reason.
  hasSource: false,
  transporterName: false,
  destinationName: false,
  createdAt: false,
  isCertified: false,
  sourceDocumentState: false,
  documentSource: false,
  latestEpaCorrectionManifestStatus: false,
  receivedDate: false,
  eManifestDaysInTransit: false,
};

/** V2's mobile view drops the same three columns. */
export const MOBILE_COLUMN_VISIBILITY = {
  ...INITIAL_COLUMN_VISIBILITY,
  epaId: false,
  wasteLineCount: false,
  vendor: false,
};
